import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo } from "react";
import CTA from "../../Constants";
import { useStorage } from "../../Hooks/useStorage";
import { message, Modal, Checkbox } from "antd";
import { getFirebaseToken } from "firebaseHelpers";
import { api, serverUrl } from "Requests/Api";
import { useAppContext } from "../../State/Global/AppContext";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import constants from "../../Constants";
import { Capacitor } from "@capacitor/core";
import {
    PushNotifications,
    Token,
    PushNotificationSchema,
    ActionPerformed,
} from "@capacitor/push-notifications";
import { Logger } from "frontend-lib/Log";
import { isNativeMobile } from "frontend-lib/Utils/Mobile";

enum DeviceType {
    WEB = "WEB",
    ANDROID = "ANDROID",
    IOS = "IOS",
}

export function useFCMNotifications(): [boolean, (show: boolean) => Promise<boolean>] {
    const [fcmNotificationConfig, setFCMNotificationConfig] = useStorage(
        {
            show: false,
            ask: "Notification" in window || ["android", "ios"].includes(Capacitor.getPlatform()),
            token: undefined,
        },
        CTA.LOCAL_STORAGE_KEYS.FCM_NOTIFICATIONS
    );

    const logger = new Logger("useFCMNotifications", serverUrl);

    // @ts-ignore
    const { appState } = useAppContext();
    const { t } = useTranslation();
    const deviceType: DeviceType = useMemo(() => {
        return Capacitor.getPlatform().toUpperCase() as DeviceType;
    }, []);

    function setNotificationsDenied(text: string, type: "error" | "warning") {
        setFCMNotificationConfig({ ...fcmNotificationConfig, show: false, token: undefined });
        message[type](text);
    }

    async function subscribeToFCMNotificationsNative(): Promise<boolean> {
        await PushNotifications.register();
        PushNotifications.addListener("registration", async (token: Token) => {
            logger.debug("FCM Token: ", { token });
            console.log("EL TOKEN ES: ", token);
            try {
                await api.users().createFCMDevice(appState.agent.id, token.value, deviceType);
                setFCMNotificationConfig({
                    ...fcmNotificationConfig,
                    show: true,
                    token: token.value,
                });
                message.success(t("notifications.activated"));
            } catch (e) {
                logger.debug("Error registering FCM notifications native ", e);
                message.error(t("notifications.registerFailed"));
                setFCMNotificationConfig({ ...fcmNotificationConfig, show: false });
                return false;
            }
        });
        PushNotifications.addListener("registrationError", (error: any) => {
            logger.error(
                `Error registering push notification on ${deviceType} of agent ${
                    appState.agent
                } with error ${JSON.stringify(error)}`
            );
            message.error(t("notifications.registerFailed"));
        });
        PushNotifications.addListener(
            "pushNotificationReceived",
            (notification: PushNotificationSchema) => {}
        );
        PushNotifications.addListener(
            "pushNotificationActionPerformed",
            (notification: ActionPerformed) => {}
        );
        return true;
    }

    async function subscribeToFCMNotificationsWeb(): Promise<boolean> {
        if (!("serviceWorker" in navigator)) {
            setNotificationsDenied(t("notifications.unsupported"), "error");
            return false;
        }

        if (!("PushManager" in window)) {
            setNotificationsDenied(t("notifications.unsupported"), "error");
            return false;
        }
        try {
            const token = await getFirebaseToken();
            await api.users().createFCMDevice(appState.agent.id, token, constants.DEVICE_TYPE.WEB);
            setFCMNotificationConfig({ ...fcmNotificationConfig, show: true, token: token });
            message.success(t("notifications.activated"));
            return true;
        } catch (e) {
            message.error(t("notifications.registerFailed"));
            setFCMNotificationConfig({ ...fcmNotificationConfig, show: false });
            return false;
        }
    }

    async function subscribeToFCMNotifications(): Promise<boolean> {
        if (deviceType === DeviceType.WEB) {
            return await subscribeToFCMNotificationsWeb();
        } else if ([DeviceType.ANDROID, DeviceType.IOS].includes(deviceType)) {
            return await subscribeToFCMNotificationsNative();
        } else {
            throw new Error(
                `Subscribe to FCM notifications not implemented for platform: ${deviceType}`
            );
        }
    }

    async function disableNotifications() {
        const token = fcmNotificationConfig.token;
        if (token === undefined) return;
        const agentId = appState.agent.id;
        await api.users().deleteFCMDevice(agentId, token);
    }

    async function requestPermissionWeb(): Promise<boolean> {
        if (!("Notification" in window)) {
            setNotificationsDenied(t("notifications.unsupported"), "error");
            return false;
        }
        const result = await Notification.requestPermission();
        if (result === "denied") {
            setNotificationsDenied(t("notifications.blocked"), "error");
            return false;
        }
        return true;
    }

    async function requestPermissionAndroid(): Promise<boolean> {
        const permissionRes = await PushNotifications.checkPermissions();
        if (permissionRes.receive === "granted") {
            return true;
        } else {
            const reqPermissionRes = await PushNotifications.requestPermissions();
            if (reqPermissionRes.receive === "denied") {
                setNotificationsDenied(t("notifications.blocked"), "error");
                return false;
            }
            return true;
        }
    }

    async function handleNotificationShow(show: boolean): Promise<boolean> {
        if (!show) {
            await disableNotifications();
            setNotificationsDenied(t("notifications.disabled"), "warning");
            return false;
        }

        let isPermissionGranted = false;
        if (deviceType === DeviceType.WEB) {
            isPermissionGranted = await requestPermissionWeb();
        } else if (deviceType === DeviceType.ANDROID) {
            isPermissionGranted = await requestPermissionAndroid();
        } else {
            isPermissionGranted = await requestPermissionAndroid();
        }

        if (isPermissionGranted) {
            return await subscribeToFCMNotifications();
        }
        return false;
    }

    function handleAskNotification() {
        let state = JSON.parse(
            window.localStorage.getItem(CTA.LOCAL_STORAGE_KEYS.FCM_NOTIFICATIONS)!
        );
        state.ask = !state.ask;
        setFCMNotificationConfig(state);
    }

    function showConfirmModal() {
        if (isNativeMobile()) {
            // When in mobile, do not use the antd modal. This allows to keep the mobile style in the app
            handleNotificationShow(true);
            return;
        }

        Modal.confirm({
            title: t("notifications.enable"),
            icon: <ExclamationCircleOutlined />,
            content: (
                <>
                    <p>{t("notifications.askText")}</p>
                    <Checkbox onChange={handleAskNotification}>
                        {t("notifications.dontAskText")}
                    </Checkbox>
                </>
            ),
            onOk() {
                handleNotificationShow(true);
            },
            onCancel() {},
        });
    }

    useEffect(() => {
        if (!fcmNotificationConfig.show && fcmNotificationConfig.ask) {
            showConfirmModal();
        }
    }, []);

    return [fcmNotificationConfig.show, handleNotificationShow];
}
